import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { toast } from 'react-hot-toast';
import {
    EyeIcon,
    TrashIcon,
    XMarkIcon,
    CheckIcon,
    ClockIcon,
} from '@heroicons/react/24/outline';

interface Contact {
    _id: string;
    name: string;
    email: string;
    phone?: string;
    subject?: string;
    message: string;
    status: string;
    adminNotes?: string;
    createdAt: string;
    repliedBy?: {
        name: string;
        email: string;
    };
    repliedAt?: string;
}

const ContactsManagement: React.FC = () => {
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>('');

    useEffect(() => {
        fetchContacts();
    }, [statusFilter]);

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const params = statusFilter ? { status: statusFilter } : {};
            const response = await api.get('/admin/contacts', { params });
            setContacts(response.data.data || []);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to fetch contacts');
        } finally {
            setLoading(false);
        }
    };

    const handleViewContact = (contact: Contact) => {
        setSelectedContact(contact);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedContact(null);
    };

    const handleStatusUpdate = async (id: string, status: string, notes?: string) => {
        try {
            await api.put(`/admin/contacts/${id}`, { status, adminNotes: notes });
            toast.success('Contact status updated');
            fetchContacts();
            if (selectedContact?._id === id) {
                setSelectedContact({ ...selectedContact, status, adminNotes: notes });
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to update contact');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) return;

        try {
            await api.delete(`/admin/contacts/${id}`);
            toast.success('Contact deleted successfully');
            fetchContacts();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete contact');
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'new': return 'bg-blue-100 text-blue-800';
            case 'read': return 'bg-yellow-100 text-yellow-800';
            case 'replied': return 'bg-green-100 text-green-800';
            case 'archived': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="spinner"></div>
                    <p className="mt-4 text-gray-600">Loading contacts...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Contact Form Submissions</h1>
                <div className="flex space-x-2">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Status</option>
                        <option value="new">New</option>
                        <option value="read">Read</option>
                        <option value="replied">Replied</option>
                        <option value="archived">Archived</option>
                    </select>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {contacts.map((contact) => (
                            <tr key={contact._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{contact.email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">{contact.phone || '-'}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="text-sm text-gray-900 truncate max-w-xs">{contact.subject || '-'}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contact.status)}`}>
                                        {contact.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {new Date(contact.createdAt).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={() => handleViewContact(contact)}
                                            className="text-blue-600 hover:text-blue-900"
                                        >
                                            <EyeIcon className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(contact._id)}
                                            className="text-red-600 hover:text-red-900"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isModalOpen && selectedContact && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal}></div>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                            <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                <h3 className="text-lg font-medium text-gray-900">Contact Details</h3>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-gray-400 hover:text-gray-500"
                                >
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="px-6 py-4">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Name</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedContact.name}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Email</label>
                                        <p className="mt-1 text-sm text-gray-900">{selectedContact.email}</p>
                                    </div>
                                    {selectedContact.phone && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Phone</label>
                                            <p className="mt-1 text-sm text-gray-900">{selectedContact.phone}</p>
                                        </div>
                                    )}
                                    {selectedContact.subject && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Subject</label>
                                            <p className="mt-1 text-sm text-gray-900">{selectedContact.subject}</p>
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Message</label>
                                        <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedContact.message}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleStatusUpdate(selectedContact._id, 'read')}
                                                className={`px-3 py-1 text-sm rounded ${selectedContact.status === 'read' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800'}`}
                                            >
                                                Read
                                            </button>
                                            <button
                                                onClick={() => handleStatusUpdate(selectedContact._id, 'replied')}
                                                className={`px-3 py-1 text-sm rounded ${selectedContact.status === 'replied' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800'}`}
                                            >
                                                Replied
                                            </button>
                                            <button
                                                onClick={() => handleStatusUpdate(selectedContact._id, 'archived')}
                                                className={`px-3 py-1 text-sm rounded ${selectedContact.status === 'archived' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-800'}`}
                                            >
                                                Archive
                                            </button>
                                        </div>
                                    </div>
                                    {selectedContact.repliedBy && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Replied By</label>
                                            <p className="mt-1 text-sm text-gray-900">
                                                {selectedContact.repliedBy.name} ({selectedContact.repliedBy.email})
                                            </p>
                                            {selectedContact.repliedAt && (
                                                <p className="mt-1 text-xs text-gray-500">
                                                    {new Date(selectedContact.repliedAt).toLocaleString()}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Date</label>
                                        <p className="mt-1 text-sm text-gray-900">
                                            {new Date(selectedContact.createdAt).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                                <button
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactsManagement;

